import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import { Table, Tabs, Spin, Alert, Tag } from "antd";
import { ArrowLeft, FileText, CheckCircle, AlertTriangle } from "lucide-react";
import { getRestoreComparisonReport } from "../../../utils/organization";
import { formatDate } from "../../../utils/luxonDateTimeHelper";
import "./RestoreReport.scss";

const { TabPane } = Tabs;

const fmt = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";

const varianceStyle = (val) =>
  val !== 0 ? { color: "#ef4444", fontWeight: 600 } : { color: "#16a34a" };

const TrialBalanceTab = ({ rows }) => {
  const columns = [
    {
      title: "Account",
      dataIndex: "accountName",
      key: "accountName",
      sorter: (a, b) => a.accountName.localeCompare(b.accountName),
      render: (val, row) => (
        <span>
          {val}
          {row.accountCode && (
            <span style={{ color: "#94a3b8", marginLeft: 6, fontSize: 11 }}>
              [{row.accountCode}]
            </span>
          )}
        </span>
      ),
    },
    {
      title: "Source Net",
      dataIndex: "netAmount",
      key: "netAmount",
      align: "right",
      sorter: (a, b) => a.netAmount - b.netAmount,
      render: fmt,
    },
    {
      title: "Restored Net",
      dataIndex: "destBalance",
      key: "destBalance",
      align: "right",
      sorter: (a, b) => a.destBalance - b.destBalance,
      render: fmt,
    },
    {
      title: "Variance",
      dataIndex: "variance",
      key: "variance",
      align: "right",
      sorter: (a, b) => a.variance - b.variance,
      render: (val) => <span style={varianceStyle(val)}>{fmt(val)}</span>,
    },
  ];

  return (
    <Table
      dataSource={rows.map((r, i) => ({ ...r, key: i }))}
      columns={columns}
      pagination={{ pageSize: 50, showSizeChanger: true }}
      rowClassName={(r) => (r.variance !== 0 ? "variance-row" : "")}
      size="small"
      scroll={{ x: 600 }}
    />
  );
};

const AgingTab = ({ rows, nameLabel }) => {
  const columns = [
    {
      title: nameLabel,
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    { title: "Source Total", dataIndex: "sourceBalance", key: "sourceBalance", align: "right", sorter: (a, b) => a.sourceBalance - b.sourceBalance, render: fmt },
    { title: "Current", dataIndex: "current", key: "current", align: "right", render: fmt },
    { title: "1–30 days", dataIndex: "days30", key: "days30", align: "right", render: fmt },
    { title: "31–60 days", dataIndex: "days60", key: "days60", align: "right", render: fmt },
    { title: "61–90 days", dataIndex: "days90", key: "days90", align: "right", render: fmt },
    { title: "91+ days", dataIndex: "days90Plus", key: "days90Plus", align: "right", render: fmt },
    { title: "Restored Total", dataIndex: "destBalance", key: "destBalance", align: "right", sorter: (a, b) => a.destBalance - b.destBalance, render: fmt },
    {
      title: "Variance",
      dataIndex: "variance",
      key: "variance",
      align: "right",
      sorter: (a, b) => a.variance - b.variance,
      render: (val) => <span style={varianceStyle(val)}>{fmt(val)}</span>,
    },
  ];

  return (
    <Table
      dataSource={rows.map((r, i) => ({ ...r, key: i }))}
      columns={columns}
      pagination={{ pageSize: 50, showSizeChanger: true }}
      rowClassName={(r) => (r.variance !== 0 ? "variance-row" : "")}
      size="small"
      scroll={{ x: 900 }}
    />
  );
};

const SummaryBanner = ({ summary, metadata }) => {
  const totalVariance =
    (summary.totalTBVariance || 0) +
    (summary.arVariance || 0) +
    (summary.apVariance || 0);
  const isBalanced = totalVariance === 0;

  return (
    <div className="rr-summary-banner">
      <div className="rr-summary-left">
        <div className="rr-summary-title">
          {isBalanced ? (
            <CheckCircle className="rr-icon green" size={20} />
          ) : (
            <AlertTriangle className="rr-icon amber" size={20} />
          )}
          <span>{isBalanced ? "All Balanced" : "Discrepancies Found"}</span>
          <Tag color={isBalanced ? "success" : "warning"} style={{ marginLeft: 8 }}>
            {metadata.type?.toUpperCase()}
          </Tag>
        </div>
        <div className="rr-summary-meta">
          <span>Backup Date: <strong>{formatDate(metadata.backupDate) || metadata.backupDate}</strong></span>
          {metadata.orgName && <span>Organisation: <strong>{metadata.orgName}</strong></span>}
          <span>Generated: <strong>{summary.generatedDate ? new Date(summary.generatedDate).toLocaleString() : "—"}</strong></span>
        </div>
      </div>
      <div className="rr-summary-cards">
        <div className="rr-card">
          <div className="rr-card-label">Trial Balance Variance</div>
          <div className="rr-card-value" style={summary.totalTBVariance !== 0 ? { color: "#ef4444" } : { color: "#16a34a" }}>
            {fmt(summary.totalTBVariance || 0)}
          </div>
        </div>
        <div className="rr-card">
          <div className="rr-card-label">AR Variance</div>
          <div className="rr-card-value" style={summary.arVariance !== 0 ? { color: "#ef4444" } : { color: "#16a34a" }}>
            {fmt(summary.arVariance || 0)}
          </div>
        </div>
        <div className="rr-card">
          <div className="rr-card-label">AP Variance</div>
          <div className="rr-card-value" style={summary.apVariance !== 0 ? { color: "#ef4444" } : { color: "#16a34a" }}>
            {fmt(summary.apVariance || 0)}
          </div>
        </div>
      </div>
    </div>
  );
};

const RestoreReport = () => {
  const { type, restoreQueueId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const userData = useSelector((state) => state.commonReducer.userData);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = userData?.token || (await getAccessTokenSilently());
        const res = await getRestoreComparisonReport(token, type, restoreQueueId);
        if (res?.data?.success) {
          setReportData(res.data);
        } else {
          setError(res?.data?.message || "Report not yet available — restore may still be in progress or CSVs were not generated.");
        }
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load report. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    if (type && restoreQueueId) fetchReport();
  }, [type, restoreQueueId]);

  return (
    <div className="rr-page">
      <div className="rr-page-header">
        <button className="rr-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
          <ArrowLeft size={16} />
          <span>Back to Organizations</span>
        </button>
        <div className="rr-page-title">
          <FileText size={22} className="rr-title-icon" />
          <h1>Restore Comparison Report</h1>
        </div>
      </div>

      {loading ? (
        <div className="rr-loading"><Spin size="large" tip="Loading report…" /></div>
      ) : error ? (
        <div className="rr-error">
          <Alert type="warning" showIcon message="Report Not Available" description={error} />
        </div>
      ) : (
        <div className="rr-content">
          <SummaryBanner summary={reportData.summary} metadata={reportData.metadata} />
          <div className="rr-tabs-container">
            <Tabs defaultActiveKey="tb" size="large">
              <TabPane tab="Trial Balance" key="tb">
                <TrialBalanceTab rows={reportData.trialBalance || []} />
              </TabPane>
              <TabPane tab="Accounts Receivable" key="ar">
                <AgingTab rows={reportData.accountsReceivable || []} nameLabel="Customer" />
              </TabPane>
              <TabPane tab="Accounts Payable" key="ap">
                <AgingTab rows={reportData.accountsPayable || []} nameLabel="Vendor / Supplier" />
              </TabPane>
            </Tabs>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestoreReport;
